// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import {
  type Facility,
  formatDate,
  type Project,
  type Shipment,
  useEvolvePaginatedGet,
  type WorkOrderId,
  type WorkOrderPaginated,
  type GetPaginatedOpts,
} from '@evolvemep/api-ts';
import { Badge, Flex, ScrollArea, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import type { SelectedShipmentItem } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/AddShipmentItemsForm';
import { WorkOrderFiltersPopover } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/WorkOrderFiltersPopover';

const WorkOrderRow = ({
  workOrder,
  selected,
  allSelected,
  selectionCount,
  onClick,
}: {
  workOrder: WorkOrderPaginated;
  selected: boolean;
  allSelected: boolean;
  selectionCount: number;
  onClick: () => void;
}) => (
  <Flex
    px="sm"
    py="xs"
    gap="xs"
    align="center"
    justify="space-between"
    style={{
      cursor: 'pointer',
      backgroundColor: selected ? 'var(--mantine-color-gray-1)' : undefined,
    }}
    onClick={onClick}
  >
    <Stack gap={2} flex={1}>
      <Text fz="sm" fw={500}>
        {workOrder.name}
      </Text>
      <Text fz="xs" c="dimmed">
        {workOrder.identifier} · {formatDate(workOrder.needByDate, 'MM/dd/yy', '—')}
      </Text>
    </Stack>
    {(allSelected || selectionCount > 0) && (
      <Badge size="sm" radius="xl">
        {allSelected ? 'All' : selectionCount}
      </Badge>
    )}
  </Flex>
);

export const WorkOrderPickerPanel = ({
  shipment,
  selectedItems,
  allSelectedWorkOrderIds,
  selectedWorkOrder,
  onSelectWorkOrder,
}: {
  shipment: Shipment;
  selectedItems: SelectedShipmentItem[];
  allSelectedWorkOrderIds: WorkOrderId[];
  selectedWorkOrder: WorkOrderId | null;
  onSelectWorkOrder: (id: WorkOrderId | null) => void;
}) => {
  const [workOrderSearch, setWorkOrderSearch] = useState('');

  const [projectFilter, setProjectFilter] = useState<Project[]>([]);
  const [facilityFilter, setFacilityFilter] = useState<Facility[]>([]);

  // The WO list is scoped to the shipment's project by default. When the user adds
  // explicit project filters, those replace the default; same for facilities.
  const woDefaultOpts = useMemo<GetPaginatedOpts<'shop/workOrder'>>(
    () => ({
      defaultConfig: {
        params: {
          searchPhrase: encodeURIComponent(workOrderSearch),
          projectIds: projectFilter.length > 0 ? projectFilter.map((p) => p.projectId) : shipment.projectId,
          facilityIds: facilityFilter.map((f) => f.facilityId),
        },
      },
    }),
    [workOrderSearch, projectFilter, facilityFilter, shipment.projectId],
  );

  const {
    data: workOrders,
    setDefaultOpts: setWoDefaultOpts,
    fetchNextPage: fetchNextWoPage,
    loading: loadingWorkOrders,
  } = useEvolvePaginatedGet('shop/workOrder', woDefaultOpts);

  useEffect(() => {
    setWoDefaultOpts(woDefaultOpts);
  }, [woDefaultOpts, setWoDefaultOpts]);

  // Compute per-WO selection counts so the WO list can show badges
  const countsByWo = useMemo(() => {
    const map = new Map<WorkOrderId, number>();
    selectedItems.forEach((sel) => {
      map.set(sel.workOrderId, (map.get(sel.workOrderId) ?? 0) + 1);
    });
    return map;
  }, [selectedItems]);

  return (
    <Stack w={280} style={{ flexShrink: 0 }} gap="xs">
      <Text fw={600} fz="sm">
        Work Order
      </Text>
      <Flex gap="xs" align="center">
        <TextInputDebounced
          flex={1}
          placeholder="Search..."
          leftSection={<EvolveIcon icon="Search" size="sm" />}
          value={workOrderSearch}
          onChange={setWorkOrderSearch}
          size="sm"
        />
        <WorkOrderFiltersPopover
          projects={projectFilter}
          setProjects={setProjectFilter}
          facilities={facilityFilter}
          setFacilities={setFacilityFilter}
        />
      </Flex>
      <ScrollArea flex={1}>
        <Stack gap={0}>
          {workOrders.map((wo) => (
            <WorkOrderRow
              key={wo.workOrderId}
              workOrder={wo}
              selected={wo.workOrderId === selectedWorkOrder}
              allSelected={allSelectedWorkOrderIds.includes(wo.workOrderId)}
              selectionCount={countsByWo.get(wo.workOrderId) ?? 0}
              onClick={() => onSelectWorkOrder(wo.workOrderId)}
            />
          ))}
          <TriggerOnVisible onVisible={fetchNextWoPage} loading={loadingWorkOrders} />
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
